import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { schoolApi } from '../../api/endpoints.js';
import { useAuth } from '../../state/AuthContext.jsx';
import { useNotice } from '../../state/NoticeContext.jsx';
import { Button, Card, Grid2, Modal, Muted, Spinner, Tag, fmtDate, useAsync } from '../../ui.jsx';

export default function SchoolDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const notice = useNotice();
  const { user, isVerified } = useAuth();
  const { data, loading, error } = useAsync(() => schoolApi.detail(slug), [slug]);
  const [starting, setStarting] = useState(false);
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState([]);
  const [addId, setAddId] = useState('');

  const MAX_CHOICES = 4;
  const CHOICE_LABELS = ['1st choice', '2nd choice', '3rd choice', '4th choice'];
  const categoryLabel = {
    coe: 'Public college of education',
    pcoe: 'Private college of education'
  };

  /** Applied from a programme row: open the picker with that one already in it. */
  const choose = (programmeId) => {
    if (!user) {
      navigate('/signup');
      return;
    }
    if (!isVerified) {
      notice.error('Verify your email before applying.');
      navigate('/verify-email');
      return;
    }
    setPicked([programmeId]);
    setAddId('');
    setPicking(true);
  };

  const addPick = (programmeId) => {
    if (!programmeId) return;
    setPicked((list) =>
      list.includes(programmeId) || list.length >= MAX_CHOICES ? list : [...list, programmeId]
    );
    setAddId('');
  };

  const removePick = (programmeId) =>
    setPicked((list) => list.filter((id) => id !== programmeId));

  /** One application to this school, carrying every programme the student ranked. */
  const startApplication = async () => {
    if (!picked.length) {
      notice.error('Choose at least one programme.');
      return;
    }
    setStarting(true);
    try {
      navigate('/apply', { state: { schoolSlug: school.slug, programmeIds: picked } });
    } catch (err) {
      notice.fromError(err);
    } finally {
      setStarting(false);
    }
  };

  if (loading) return <Spinner label="Loading school" />;
  if (error || !data) {
    return (
      <Card>
        <h2>College not available</h2>
        <Muted>That college is not on Entrance, or it is no longer listed.</Muted>
        <Button onClick={() => navigate('/schools')}>Back to training colleges</Button>
      </Card>
    );
  }

  const { school, programmes } = data;
  const available = programmes.filter((programme) => !picked.includes(programme.id));

  return (
    <>
      <div className="row sp" style={{ marginBottom: 4 }}>
        <div>
          <h2 style={{ margin: 0 }}>{school.name}</h2>
          <Muted>{school.city} · {categoryLabel[school.category] || school.category}</Muted>
        </div>
        <div style={{ textAlign: 'right' }}>
          <Tag tone={school.isClosed ? 'no' : school.deadline ? 'ok' : 'mid'}>{school.statusLabel}</Tag>
        </div>
      </div>

      <Card>
        <Grid2>
          <div>
            <p className="hd">Application fee</p>
            <p className="mu">
              GH{school.applicationFee ?? 'Not set'}
              {school.feeEstimated ? ' (estimated)' : ''}
            </p>

            <p className="hd">Deadline</p>
            <p className="mu">
              {fmtDate(school.deadline)}
              {school.deadlineNote ? <><br />{school.deadlineNote}</> : null}
            </p>

            {school.payment ? (
              <>
                <p className="hd">How to pay</p>
                <p className="mu">
                  {school.payment.collecting
                    ? <>
                        {school.payment.payee}
                        {school.payment.merchantId ? ` · ${school.payment.merchantId}` : ''}
                        {school.payment.networks?.length ? ` · ${school.payment.networks.join(', ')}` : ''}
                      </>
                    : school.paymentVia || 'Payments are not enabled for this college yet.'}
                </p>
              </>
            ) : null}

            {school.entranceExamNote ? (
              <>
                <p className="hd">Entrance exam</p>
                <p className="mu">{school.entranceExamNote}</p>
              </>
            ) : null}
          </div>

          <div>
            {school.requirements?.length ? (
              <>
                <p className="hd">Entrance requirements</p>
                <ul className="req">
                  {school.requirements.map((r) => <li key={r}>{r}</li>)}
                </ul>
              </>
            ) : (
              <>
                <p className="hd">Entrance requirements</p>
                <Muted>This school has not published its requirements on Entrance yet.</Muted>
              </>
            )}

            {school.feePayingAvailable && school.feePayingNote ? (
              <div className="note">{school.feePayingNote}</div>
            ) : null}
          </div>
        </Grid2>

        {school.sources?.length ? (
          <details>
            <summary>Where these requirements come from</summary>
            {school.sources.map(({ label, url }) => (
              <p key={url}>
                <a href={url} target="_blank" rel="noreferrer noopener">{label}</a>
              </p>
            ))}
          </details>
        ) : null}
      </Card>

      <h2 style={{ margin: '26px 0 12px' }}>Programmes</h2>
      {programmes.length === 0 ? (
        <Card>
          <Muted>
            {school.name} has not published its programme list and cut-offs on Entrance yet. Nothing is
            shown here rather than a guess.
          </Muted>
        </Card>
      ) : (
        <Card>
          {programmes.map((programme) => (
            <div className="prog" key={programme.id} style={{ alignItems: 'flex-start' }}>
              <i className="ub">{school.shortName}</i>
              <div style={{ flex: 1, minWidth: 0 }}>
                <b>{programme.name}</b>
                <div className="mu">{programme.fieldLabel || programme.field}</div>
                <div className="mu">
                  {programme.ruleText}
                </div>
                {programme.note ? <div className="mu">{programme.note}</div> : null}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="mu">
                  Cut-off {programme.cutOff ?? 'Not set'}
                  {programme.cutOffEstimated ? ' (est.)' : ''}
                </div>
                {programme.cutOffFeePaying ? (
                  <div className="mu">Fee-paying {programme.cutOffFeePaying}</div>
                ) : null}
                <div style={{ marginTop: 8 }}>
                  <Button
                    size="sm"
                    busy={starting}
                    disabled={school.isClosed}
                    onClick={() => choose(programme.id)}
                  >
                    {school.isClosed ? 'Closed' : 'Apply'}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </Card>
      )}

      {picking ? (
        <Modal
          title={`Your ${school.shortName} programme choices`}
          labelledBy="picking-title"
          onClose={() => setPicking(false)}
          footer={
            <>
              <Button busy={starting} disabled={!picked.length} onClick={startApplication}>
                Apply to {school.shortName} with {picked.length}{' '}
                {picked.length === 1 ? 'programme' : 'programmes'}
              </Button>
              <Button variant="s" onClick={() => setPicking(false)}>Cancel</Button>
            </>
          }
        >
          <div className="row sp" style={{ marginBottom: 10 }}>
            <Muted>
              Only the programmes you have chosen are shown, in your order of preference. Add another
              below if you want more than one.
            </Muted>
            <Tag tone={picked.length ? 'ok' : 'mid'}>
              {picked.length} of {MAX_CHOICES} chosen
            </Tag>
          </div>

          {picked.length ? (
            picked.map((programmeId, index) => {
              const programme = programmes.find((item) => item.id === programmeId);
              if (!programme) return null;
              return (
                <div className="prog" key={programme.id}>
                  <Tag tone="ok">{CHOICE_LABELS[index]}</Tag>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <b>{programme.name}</b>
                    <div className="mu">
                      Cut-off {programme.cutOff ?? 'not set'}
                      {programme.cutOffEstimated ? ' (est.)' : ''} ·{' '}
                      {programme.fieldLabel || programme.field}
                    </div>
                  </div>
                  <Button size="sm" variant="s" onClick={() => removePick(programme.id)}>
                    Remove
                  </Button>
                </div>
              );
            })
          ) : (
            <div className="note">No programme chosen yet. Add one below to continue.</div>
          )}

          {picked.length < MAX_CHOICES && available.length ? (
            <div className="row" style={{ flexWrap: 'nowrap', alignItems: 'flex-end', marginTop: 14 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={{ marginBottom: 0 }}>
                  Add another programme
                  <select value={addId} onChange={(event) => setAddId(event.target.value)}>
                    <option value="">Choose a programme</option>
                    {available.map((programme) => (
                      <option key={programme.id} value={programme.id}>{programme.name}</option>
                    ))}
                  </select>
                </label>
              </div>
              <Button size="sm" variant="s" disabled={!addId} onClick={() => addPick(addId)}>
                Add
              </Button>
            </div>
          ) : null}
        </Modal>
      ) : null}
    </>
  );
}
